import React, { useState, useEffect } from 'react';
import { useIntl } from 'react-intl';
import { withTheme } from 'styled-components';
import PropTypes from 'prop-types';
import { post } from 'shared-components/src/utils/http';
import auth from 'shared-components/src/utils/auth';
import { getPayAccountUrl } from 'shared-components/src/utils/whitelabel';
import { assignSafeUrl } from 'shared-components/src/utils/safeUrl';
import { verifyRegisterValidations } from 'shared-components/src/validations/cardholderValidations';
import { isCAMSDefaultHost } from '../../../../utils/cams';
import { withLang } from '../../../../utils/locale';
import { errorMessage } from '../../../../utils/validationHandlers';
import {
  PageWrapper,
  BlobShape,
  GeometricShapes,
  Shape,
  CamsHeader,
  LogoContainer,
  HeaderNav,
  HeaderLink,
  LanguageToggle,
  MainContent,
  Wrapper,
  Title,
  Subtitle,
  FormContainer,
  InputLabel,
  Input,
  InputWrapper,
  InputWithTooltip,
  TooltipContainer,
  HelpIconButton,
  TooltipBubble,
  Button,
  ErrorMessage,
  SuccessMessage,
  FieldErrorMessage,
  LoginLink,
} from '../CamsLogin/styles';
import CamsPageFooter from '../CamsLogin/CamsPageFooter';

// Email validation regex
const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const CamsRegister = ({ theme, locale, setLocale }) => {
  const intl = useIntl();
  const page = 'camsregister';

  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [passwordConfirmation, setPasswordConfirmation] = useState('');
  // Pre-populate email from sessionStorage (set during CAMS login)
  const [email, setEmail] = useState(() => sessionStorage.getItem('register_email') || '');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [redirectMessage, setRedirectMessage] = useState(null);
  const [fieldErrors, setFieldErrors] = useState({});
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const currentLocale = locale || 'en';
  const toggleLocale = currentLocale === 'en' ? 'fr' : 'en';

  // Check if user has a valid session from CAMS login
  useEffect(() => {
    if (!auth.isAuthenticated()) {
      window.location.pathname = '/cams';
    }
  }, []);

  const handleLocaleChange = () => {
    if (setLocale) {
      sessionStorage.setItem('locale', toggleLocale);
      setLocale(toggleLocale);
    }
  };

  const validateEmail = emailValue =>
    Boolean(emailValue?.trim()) && EMAIL_REGEX.test(emailValue);

  const validateForm = () => {
    const errors = {};

    // Validate username
    if (!verifyRegisterValidations.username(username)) {
      errors.username = username === ''
        ? `${page}-username-required`
        : `${page}-username-invalid`;
    }

    // Validate password
    if (!verifyRegisterValidations.password(password)) {
      errors.password = password === ''
        ? `${page}-password-required`
        : `${page}-password-invalid`;
    }

    // Validate password confirmation
    if (password !== passwordConfirmation || passwordConfirmation === '') {
      errors.passwordConfirmation = passwordConfirmation === ''
        ? `${page}-passwordconfirm-required`
        : `${page}-passwordconfirm-mismatch`;
    }

    // Validate email
    if (!validateEmail(email)) {
      errors.email = email === ''
        ? `${page}-email-required`
        : `${page}-email-invalid`;
    }

    setFieldErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const getErrorMessage = errorCode => {
    const errorMap = {
      username_not_available: `${page}-username-taken`,
      invalid_password: `${page}-password-invalid`,
      invalid_email: `${page}-email-invalid`,
      email_already_exists: `${page}-email-taken`,
      registration_failed: `${page}-registration-failed`,
      unauthorized: `${page}-session-expired`,
    };
    return intl.messages[errorMap[errorCode]] ? errorMap[errorCode] : `${page}-registration-failed`;
  };

  const handleSubmit = async e => {
    e.preventDefault();
    setError(null);

    if (!validateForm()) {
      return;
    }

    setLoading(true);

    try {
      const registerPayload = {
        username,
        password,
        password_confirmation: passwordConfirmation,
        email,
      };

      await post('/api/v1/cardholder_portal/cams_register', registerPayload);

      // Show confirmation message, then redirect to login on payaccount.io after a short delay
      setRedirectMessage(`${page}-registration-success`);
      setTimeout(() => {
        const payAccountUrl = getPayAccountUrl();
        const loginUrl = withLang(payAccountUrl ? `${payAccountUrl}/login` : '/login', currentLocale);
        if (!assignSafeUrl(loginUrl)) {
          assignSafeUrl(withLang('/login', currentLocale));
        }
      }, 5000);
    } catch (err) {
      setLoading(false);
      if (err.code) {
        setError(getErrorMessage(err.code));
      } else if (err.message) {
        setError(err.message);
      } else {
        setError(`${page}-registration-failed`);
      }
    }
  };

  const handleKeyUp = e => {
    if (e.keyCode === 13) {
      handleSubmit(e);
    }
  };

  const isFormValid = () => username && password && passwordConfirmation && email;

  const usernameTooltipText = intl.messages[`${page}-username-tooltip`];
  const passwordTooltipText = intl.messages[`${page}-password-tooltip`];

  // Get logo from theme
  const logoUrl = theme?.brand?.logo_url || theme?.brand?.logo;

  // Get payaccount login URL for "already have account" link.
  // withLang carries the selected language across the domain hop (sessionStorage does not).
  const payAccountLoginUrl = withLang(`${getPayAccountUrl()}/login`, currentLocale);

  // Check if we should show default decorative elements
  const showDecorations = isCAMSDefaultHost();

  return (
    <PageWrapper>
      {/* Large blob shapes at top corners - only for default hostnames */}
      {showDecorations && (
        <>
          <BlobShape className="green-blob" />
          <BlobShape className="blue-blob" />
        </>
      )}

      {/* Geometric background shapes - only for default hostnames */}
      {showDecorations && (
        <GeometricShapes>
          <Shape className="circle" color="#6EE7B7" style={{ top: '8%', left: '20%', width: '16px', height: '16px' }} opacity={0.6} />
          <Shape className="circle" color="#5C6BC0" style={{ top: '12%', left: '16%', width: '8px', height: '8px' }} opacity={0.7} />
          <Shape className="hollow-square" color="#6EE7B7" style={{ top: '8%', left: '45%', width: '24px', height: '24px', transform: 'rotate(12deg)' }} opacity={0.8} />
          <Shape className="solid-square" color="#5C6BC0" style={{ top: '6%', left: '48%', width: '32px', height: '32px', transform: 'rotate(-12deg)' }} opacity={0.7} />
          <Shape className="circle" color="#6EE7B7" style={{ top: '14%', left: '52%', width: '8px', height: '8px' }} opacity={0.6} />
          <Shape className="circle" color="#5C6BC0" style={{ top: '6%', right: '10%', width: '12px', height: '12px' }} opacity={0.8} />
          <Shape className="circle" color="#6EE7B7" style={{ top: '18%', right: '6%', width: '10px', height: '10px' }} opacity={0.7} />
          <Shape className="circle" color="#6EE7B7" style={{ top: '22%', right: '8%', width: '6px', height: '6px' }} opacity={0.7} />
          <Shape className="hollow-square" color="#5C6BC0" style={{ top: '35%', left: '8%', width: '16px', height: '16px', transform: 'rotate(-12deg)' }} opacity={0.7} />
          <Shape className="hollow-square" color="#6EE7B7" style={{ bottom: '20%', left: '6%', width: '20px', height: '20px', transform: 'rotate(12deg)' }} opacity={0.8} />
          <Shape className="circle" color="#5C6BC0" style={{ bottom: '10%', left: '15%', width: '32px', height: '32px' }} opacity={0.7} />
          <Shape className="circle" color="#6EE7B7" style={{ bottom: '16%', left: '20%', width: '8px', height: '8px' }} opacity={0.6} />
          <Shape className="circle" color="#6EE7B7" style={{ bottom: '12%', right: '12%', width: '12px', height: '12px' }} opacity={0.7} />
          <Shape className="circle" color="#6EE7B7" style={{ bottom: '8%', right: '15%', width: '8px', height: '8px' }} opacity={0.7} />
        </GeometricShapes>
      )}

      {/* Header */}
      <CamsHeader>
        <LogoContainer>
          {logoUrl && <img src={logoUrl} alt="Logo" />}
        </LogoContainer>
        <HeaderNav>
          <HeaderLink
            href="https://help.mycardholdersupport.com/portal/kb/cs"
            target="_blank"
            rel="noopener noreferrer"
          >
            {intl.messages['camslogin-knowledge-base']}
          </HeaderLink>
          <LanguageToggle onClick={handleLocaleChange}>
            {toggleLocale.toUpperCase()}
          </LanguageToggle>
        </HeaderNav>
      </CamsHeader>

      {/* Main content */}
      <MainContent>
        <Wrapper>
          <Title>{intl.messages[`${page}-title`]}</Title>
          <Subtitle>{intl.messages[`${page}-subtitle`]}</Subtitle>

          <FormContainer onSubmit={handleSubmit} onKeyUp={handleKeyUp}>
            {/* Email */}
            <InputWrapper>
              <InputLabel htmlFor="email">
                {intl.messages[`${page}-email-label`]}
              </InputLabel>
              <Input
                type="email"
                id="email"
                placeholder={intl.messages[`${page}-email-placeholder`]}
                value={email}
                onChange={e => {
                  setEmail(e.target.value.trim());
                  if (fieldErrors.email) {
                    setFieldErrors(prev => ({ ...prev, email: null }));
                  }
                }}
                className={fieldErrors.email ? 'is-invalid' : ''}
                disabled={loading}
                autoComplete="email"
              />
              {fieldErrors.email && (
                <FieldErrorMessage>{errorMessage(intl, fieldErrors.email)}</FieldErrorMessage>
              )}
            </InputWrapper>

            {/* Username */}
            <InputWrapper>
              <InputLabel htmlFor="username">
                {intl.messages[`${page}-username-label`]}
              </InputLabel>
              <InputWithTooltip>
                <Input
                  type="text"
                  id="username"
                  placeholder={intl.messages[`${page}-username-placeholder`]}
                  value={username}
                  onChange={e => {
                    setUsername(e.target.value.trim());
                    if (fieldErrors.username) {
                      setFieldErrors(prev => ({ ...prev, username: null }));
                    }
                  }}
                  className={fieldErrors.username ? 'is-invalid' : ''}
                  disabled={loading}
                  autoComplete="username"
                  minLength={3}
                  maxLength={30}
                />
                <TooltipContainer>
                  <HelpIconButton type="button" aria-label="Username help">?</HelpIconButton>
                  <TooltipBubble>{usernameTooltipText}</TooltipBubble>
                </TooltipContainer>
              </InputWithTooltip>
              {fieldErrors.username && (
                <FieldErrorMessage>{errorMessage(intl, fieldErrors.username)}</FieldErrorMessage>
              )}
            </InputWrapper>

            {/* Password */}
            <InputWrapper>
              <InputLabel htmlFor="password">
                {intl.messages[`${page}-password-label`]}
              </InputLabel>
              <InputWithTooltip>
                <Input
                  type={showPassword ? 'text' : 'password'}
                  id="password"
                  placeholder={intl.messages[`${page}-password-placeholder`]}
                  value={password}
                  onChange={e => {
                    setPassword(e.target.value.trim());
                    if (fieldErrors.password) {
                      setFieldErrors(prev => ({ ...prev, password: null }));
                    }
                  }}
                  className={fieldErrors.password ? 'is-invalid' : ''}
                  disabled={loading}
                  autoComplete="new-password"
                  minLength={12}
                />
                <TooltipContainer>
                  <HelpIconButton type="button" aria-label="Password help">?</HelpIconButton>
                  <TooltipBubble>{passwordTooltipText}</TooltipBubble>
                </TooltipContainer>
              </InputWithTooltip>
              {fieldErrors.password && (
                <FieldErrorMessage>{errorMessage(intl, fieldErrors.password)}</FieldErrorMessage>
              )}
            </InputWrapper>

            {/* Confirm Password */}
            <InputWrapper>
              <InputLabel htmlFor="passwordConfirmation">
                {intl.messages[`${page}-passwordconfirm-label`]}
              </InputLabel>
              <Input
                type={showConfirmPassword ? 'text' : 'password'}
                id="passwordConfirmation"
                placeholder={intl.messages[`${page}-passwordconfirm-placeholder`]}
                value={passwordConfirmation}
                onChange={e => {
                  setPasswordConfirmation(e.target.value.trim());
                  if (fieldErrors.passwordConfirmation) {
                    setFieldErrors(prev => ({ ...prev, passwordConfirmation: null }));
                  }
                }}
                className={fieldErrors.passwordConfirmation ? 'is-invalid' : ''}
                disabled={loading}
                autoComplete="new-password"
                minLength={12}
              />
              {fieldErrors.passwordConfirmation && (
                <FieldErrorMessage>{errorMessage(intl, fieldErrors.passwordConfirmation)}</FieldErrorMessage>
              )}
            </InputWrapper>

            <Button
              type="submit"
              disabled={loading || !isFormValid() || Boolean(redirectMessage)}
              className={loading ? 'is-loading' : ''}
            >
              {loading ? (
                <svg
                  width="22"
                  height="22"
                  viewBox="0 0 24 24"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    opacity=".2"
                    fillRule="evenodd"
                    clipRule="evenodd"
                    d="M12 19a7 7 0 1 0 0-14 7 7 0 0 0 0 14m0 3c5.523 0 10-4.477 10-10S17.523 2 12 2 2 6.477 2 12s4.477 10 10 10"
                    fill="currentColor"
                  />
                  <path
                    d="M12 22c5.523 0 10-4.477 10-10h-3a7 7 0 0 1-7 7zM2 12C2 6.477 6.477 2 12 2v3a7 7 0 0 0-7 7z"
                    fill="currentColor"
                  />
                </svg>
              ) : (
                intl.messages[`${page}-submit-button`]
              )}
            </Button>

            {redirectMessage && (
              <SuccessMessage>
                <svg
                  width="20"
                  height="20"
                  viewBox="0 0 24 24"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <circle cx="12" cy="12" r="10" stroke="#155724" strokeWidth="2" />
                  <path d="M8 12l2.5 2.5L16 9" stroke="#155724" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
                <span>{intl.messages[redirectMessage]}</span>
              </SuccessMessage>
            )}

            {error && (
              <ErrorMessage>
                <svg
                  width="20"
                  height="20"
                  viewBox="0 0 24 24"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <circle cx="12" cy="12" r="10" stroke="#721c24" strokeWidth="2" />
                  <line x1="12" y1="8" x2="12" y2="12" stroke="#721c24" strokeWidth="2" />
                  <circle cx="12" cy="16" r="1" fill="#721c24" />
                </svg>
                <span>{errorMessage(intl, error)}</span>
              </ErrorMessage>
            )}

            <LoginLink href={payAccountLoginUrl}>
              {intl.messages[`${page}-login-link`]}
            </LoginLink>
          </FormContainer>
        </Wrapper>

        {/* Footer */}
        <CamsPageFooter theme={theme} locale={locale} />
      </MainContent>
    </PageWrapper>
  );
};

CamsRegister.propTypes = {
  theme: PropTypes.object,
  locale: PropTypes.string,
  setLocale: PropTypes.func,
};

export default withTheme(CamsRegister);